import type { ReactElement } from "react"
import React, { useState } from "react"

import { useTranslation } from "react-i18next"
import { useQuery } from "react-query"
import { useParams } from "react-router-dom"

import { Grid } from "@mui/material"
import type { AxiosError } from "axios"
import moment from "moment"

import { AccordionHeader } from "../../../components/UI/AccordionHeader"
import { LogViewer } from "../../../components/data-display/LogViewer"
import { KonanAPI } from "../../../services/KonanAPI"
import { KonanTimeHelper } from "../../../utils/genericHelpers"
import type { ScriptTestingLogsProps } from "../Interfaces"

import styles from "../Scripts.module.scss"

export function ScriptTestingLogs(props: Readonly<ScriptTestingLogsProps>): ReactElement {
  const {
    logs,
    isTestScriptMutationLoading,
    setIsLogsExpanded,
    setIsConsoleExpanded,
    consoleLogs,
    isConsoleExpanded,
    isLogsExpanded,
    canShowDetails,
  } = props

  const { t } = useTranslation("scripts", {
    keyPrefix: "components.testingLogs",
  })

  return (
    <Grid container item flexDirection={"column"} gap={0.5}>
      {canShowDetails && (
        <Grid item>
          <AccordionHeader
            title={t("scriptOutput")}
            isAccordionExpanding={isConsoleExpanded}
            actionButtonProps={{
              onClick: () => setIsConsoleExpanded((prev) => !prev),
            }}
          />

          {isConsoleExpanded && (
            <LogViewer
              enableSearch={false}
              customBorderRadius="0px 0px 8px 8px"
              data={consoleLogs && consoleLogs?.length > 0 ? consoleLogs : []}
              isLoading={!!isTestScriptMutationLoading}
              minHeight="150px"
            />
          )}
        </Grid>
      )}

      <Grid item>
        <AccordionHeader
          title={t("testLogs")}
          isAccordionExpanding={isLogsExpanded}
          actionButtonProps={{
            onClick: () => setIsLogsExpanded((prev) => !prev),
          }}
        />

        {isLogsExpanded && (
          <Grid item className={styles.accordionlogViewer}>
            <LogViewer
              customBorderRadius="8px"
              data={logs && logs?.length > 0 ? logs : []}
              isLoading={!!isTestScriptMutationLoading}
              minHeight="150px"
            />
          </Grid>
        )}
      </Grid>
    </Grid>
  )
}

/**
 * Historical logs dialog responsible for showing logs of current script up to the last 7 days
 *
 * @param {string} uuid
 * @returns {React.ReactElement}
 */
export function ScriptLogs(props: Readonly<{ uuid: string }>): React.ReactElement {
  const { uuid } = props
  const { id: projectId } = useParams<{ id: string }>()

  const konanTime = new KonanTimeHelper()

  const [startDate, setStartDate] = useState(konanTime.adjustDate(moment().subtract(6, "days"), "start"))
  const [endDate, setEndDate] = useState(konanTime.adjustDate(moment(), "end"))

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const { isLoading, data } = useQuery<any, AxiosError>(
    ["script-logs", projectId, startDate, endDate, uuid],
    () =>
      KonanAPI.fetchScriptLogs({
        start_date: startDate?.toISOString() as string,
        end_date: endDate?.toISOString() as string,
        project_uuid: projectId as string,
        script_uuid: uuid,
      }),
    { enabled: !!startDate && !!endDate, refetchInterval: 3000 },
  )

  return (
    <Grid item xs={12} className={styles.logViewerContainer}>
      <LogViewer
        customBorderRadius="8px"
        data={data?.logs ?? []}
        isLoading={isLoading}
        minHeight={"unset"}
        enableDatePicker
        startDate={startDate}
        endDate={endDate}
        onStartDateChange={setStartDate}
        onEndDateChange={setEndDate}
        isOutSideRange={konanTime.lastWeekOnly}
      />
    </Grid>
  )
}
